/**
 * PayrollAdjustmentsPage — "Rregullimet e pagës".
 *
 * Bashkon çka ishin tri faqe të veçanta (Bonuset, Avancet, Rimbursimet) dhe
 * shton ndalesat. Arsyeja nuk është vetëm rregull në meny: tri module të ndara
 * do të thoshin tri rrugë drejt pagës, tre butona "bart" dhe asnjë vend ku të
 * shihej çka i shtohet e çka i hiqet një punëtori për një muaj.
 *
 * Tabs-at janë filtër kategorie mbi TË NJËJTËN tabelë — jo katër tabela. Prandaj
 * kolona "Kategoria" mbetet edhe brenda një tabi: ajo çka sheh te "Të gjitha"
 * është e njëjta pamje, vetëm e pafiltruar.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Stack, Chip, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Snackbar, Alert, Tabs, Tab, Divider,
  CircularProgress, Card, CardActionArea, Grid, TableContainer, Checkbox,
} from '@mui/material';
import AddIcon         from '@mui/icons-material/Add';
import EditIcon        from '@mui/icons-material/Edit';
import DeleteIcon      from '@mui/icons-material/DeleteOutline';
import SearchIcon      from '@mui/icons-material/Search';
import AttachFileIcon  from '@mui/icons-material/AttachFile';
import DownloadIcon    from '@mui/icons-material/Download';
import GridOnIcon      from '@mui/icons-material/GridOn';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TuneIcon        from '@mui/icons-material/Tune';
import FactCheckOutlinedIcon  from '@mui/icons-material/FactCheckOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon      from '@mui/icons-material/CancelRounded';
import SendRoundedIcon        from '@mui/icons-material/SendRounded';
// xlsx-js-style: i njëjti API si `xlsx`, por i shkruan VËRTET stilet. Me `xlsx`
// të thjeshtë ato shpërfillen në heshtje — skedari dilte mur shifrash pa kokë,
// pa ngjyra, pa format numrash.
import * as XLSX       from 'xlsx-js-style';
import jsPDF           from 'jspdf';
import autoTable       from 'jspdf-autotable';
import PageHeader      from '../components/common/PageHeader';
import { drawCompanyLogo } from '../utils/payrollRegisterExport';
import apiService, { authFetch } from '../services/apiServices';
import ConfirmDialog from '../components/common/ConfirmDialog';
import StatusChip from '../components/common/StatusChip';
import WorkerPicker, { normalize, workerId, workerName } from '../components/common/WorkerPicker';
import AdjustmentReviewDialog from '../components/payroll/AdjustmentReviewDialog';
import AdjustmentBulkDecideDialog from '../components/payroll/AdjustmentBulkDecideDialog';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../config/permissions/permissionsConfig';
import globals from '../services/globals';
import { MONTHS_SQ } from '../utils/period';
import { activeWorkers as pickActiveWorkers } from '../utils/workerStatus';
import {
  CATEGORIES, CATEGORY_META, TYPES, STATUSES, REPAYMENT_METHODS,
  DOCUMENT_CATEGORIES, typeLabel, statusMeta, canRun,
} from '../config/payrollAdjustments';

const fmt = (n) => Number(n || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL') : '—');
const periodOf = (r) => (r.periodYear && r.periodMonth ? `${MONTHS_SQ[r.periodMonth - 1]} ${r.periodYear}` : '—');
const periodKeyOf = (r) => (r.periodYear && r.periodMonth ? `${r.periodYear}-${String(r.periodMonth).padStart(2, '0')}` : '');

/** Çka thuhet pas një hapi — veprimi, jo "u ruajt me sukses". */
const DECIDED_MSG = {
  submit:  'U dërgua për aprovim.',
  approve: 'U aprovua — hyn në pagën e periudhës.',
  reject:  'U refuzua. Arsyeja i shkoi punonjësit.',
  revoke:  'Aprovimi u hoq — rregullimi pret sërish vendim.',
};

const now = new Date();
const EMPTY = {
  workerId: '', category: '', type: '', description: '', amount: '',
  effectiveDate: new Date().toISOString().slice(0, 10),
  periodYear: now.getFullYear(), periodMonth: now.getMonth() + 1,
  // Statusi NUK është më fushë e formularit: e vendos veprimi i ruajtjes
  // ("Ruaj si draft" ose "Dërgo për aprovim") dhe pastaj vetëm procesi.
  status: 'draft', repaymentMethod: 'lump_sum', installments: 1, repaidAmount: 0,
  repaysAdjustmentId: null,
};

export default function PayrollAdjustmentsPage() {
  const { can, hasAnyRole } = usePermissions();

  /**
   * Konteksti i vendimmarrësit — pasqyron `adjustmentActor` te serveri.
   *
   * `isDecider`: leja e aprovimit të pagës, ose një rol shqyrtues. Të dyja, sepse
   * RBAC-u lejon role të personalizuara pa asnjë nga rolet e njohura, dhe HR-ja
   * standarde e ka rolin pa lejen e veçantë.
   *
   * `workerId` duhet për rregullin që askush nuk vendos për rreshtin e vet. Kur
   * llogaria nuk është e lidhur me punëtor, mbetet null dhe gardhi qëndron te
   * serveri — ku edhe duhet të rrijë.
   */
  const actor = useMemo(() => {
    const u = globals().getLogedInUser();
    const d = u?.data || u || {};
    return {
      userId:   d.id ?? d.Id ?? null,
      workerId: d.worker_id ?? d.workerId ?? null,
      isDecider: can(PERMISSIONS.PAYROLL_APPROVE_COMPANY) || can(PERMISSIONS.PAYROLL_MANAGE)
        || hasAnyRole(['super_admin', 'company_admin', 'hr_manager', 'payroll_manager', 'team_lead']),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [rows,     setRows]     = useState([]);
  const [workers,  setWorkers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState('all');
  const [search,   setSearch]   = useState('');
  const [fWorker,  setFWorker]  = useState('');
  const [fStatus,  setFStatus]  = useState('');
  const [fPeriod,  setFPeriod]  = useState('');
  const [form,     setForm]     = useState(EMPTY);
  const [dlgOpen,  setDlgOpen]  = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snack,    setSnack]    = useState({ open: false, msg: '', sev: 'success' });
  const fileRef = useRef(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [company,   setCompany]   = useState({});
  // Vendimi: paneli i shqyrtimit për një rresht, ose dialogu i grupit.
  const [review,   setReview]   = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulk,     setBulk]     = useState(null);

  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, wRes, sRes] = await Promise.all([
        apiService().get('payrollAdjustments'),
        apiService().get('workers'),
        apiService().get('settings'),
      ]);
      setRows(aRes?.data   || []);
      setWorkers(wRes?.data || []);
      setCompany(sRes?.data?.[0] || {});
    } catch (e) {
      notify(`Nuk u ngarkuan rregullimet: ${e.message}`, 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // `pickActiveWorkers`, jo `activeWorkers`: emri i importuar mbulohej nga ky
  // `const` me të njëjtin emër, ndaj thirrja brenda `useMemo` shkonte te vetë
  // konstantja që sapo po deklarohej — zona e vdekur kohore. Rezultati ishte
  // `ReferenceError` në render, pra faqja binte sapo hapej.
  const activeWorkers = useMemo(() =>
    pickActiveWorkers(workers)
      .sort((a, b) => workerName(a).localeCompare(workerName(b), 'sq'))
  , [workers]);

  const periods = useMemo(() => {
    const keys = new Set(rows.map(periodKeyOf).filter(Boolean));
    return Array.from(keys).sort((a, b) => b.localeCompare(a)).map(k => {
      const [y, m] = k.split('-');
      return { key: k, label: `${MONTHS_SQ[Number(m) - 1]} ${y}` };
    });
  }, [rows]);

  const filtered = useMemo(() => {
    const terms = normalize(search).trim().split(/\s+/).filter(Boolean);
    return rows.filter(r => {
      if (tab !== 'all' && r.category !== tab) return false;
      const hay = normalize(`${r.workerName || ''} ${r.description || ''} ${typeLabel(r.category, r.type)}`);
      const words = hay.split(/\s+/);
      if (terms.length && !terms.every(t => words.some(w => w.startsWith(t)) || hay.includes(t))) return false;
      if (fWorker && String(r.workerId) !== String(fWorker)) return false;
      if (fStatus && r.status !== fStatus) return false;
      if (fPeriod && periodKeyOf(r) !== fPeriod) return false;
      return true;
    });
  }, [rows, tab, search, fWorker, fStatus, fPeriod]);

  // Totalet ndahen sipas drejtimit, jo në një shifër: një shumë e vetme që
  // përzien shtesat me ndalesat nuk do të thoshte asgjë.
  const totals = useMemo(() => filtered.reduce((a, r) => {
    const amt = Number(r.amount) || 0;
    if (r.status === 'approved' || r.status === 'processed') {
      if (r.category === 'addition')           a.additions += amt;
      else if (r.category === 'reimbursement') a.reimbursements += amt;
      else                                     a.deductions += amt;
    }
    if (r.status === 'pending') a.pending += 1;
    return a;
  }, { additions: 0, deductions: 0, reimbursements: 0, pending: 0 }), [filtered]);

  const countBy = useCallback((cat) => rows.filter(r => cat === 'all' || r.category === cat).length, [rows]);
  const clearFilters = () => { setSearch(''); setFWorker(''); setFStatus(''); setFPeriod(''); };

  // ── Radha e shqyrtimit ─────────────────────────────────────────────────────
  // Sa presin vendim NË TË GJITHË bazën, jo brenda filtrave: një kërkesë që
  // pret nuk zhduket sepse dikush zgjodhi një tab tjetër.
  const waiting = useMemo(() => rows.filter(r => r.status === 'pending'), [rows]);
  const onlyPending = fStatus === 'pending';
  const showQueue = () => { clearFilters(); setTab('all'); setFStatus('pending'); };

  // Zgjedhja mban ID-të, jo rreshtat: pas një rifreskimi rreshtat janë objekte
  // të reja, dhe një zgjedhje e mbajtur me referencë do të kishte humbur.
  const selectedRows = useMemo(
    () => rows.filter(r => selected.includes(r.id)),
    [rows, selected],
  );
  const toggleRow = (id) =>
    setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  // "Zgjedh të gjitha" nuk zgjedh çka nuk vendoset: rreshtat e procesuar ose të
  // vetvetes do të mbeteshin gjithnjë të pavendosur, dhe kutia e kokës do të
  // tregonte një zgjedhje që nuk kryhet.
  const decidable = useMemo(
    () => filtered.filter(r => canRun('approve', r, actor) || canRun('reject', r, actor)),
    [filtered, actor],
  );
  const allPicked = decidable.length > 0 && decidable.every(r => selected.includes(r.id));
  const toggleAll = () =>
    setSelected(allPicked ? [] : decidable.map(r => r.id));

  // ── Eksporti ───────────────────────────────────────────────────────────────
  // Eksportohet ajo që SHIHET: tabi aktiv plus filtrat. Një skedar që përmban
  // gjithnjë krejt bazën, ndërsa ekrani tregon një kategori, do të ishte një
  // kurth i heshtur për këdo që e dërgon më tej.
  const tabLabel = tab === 'all' ? 'Të gjitha' : CATEGORY_META[tab].label;
  const activeFilters = () => {
    const f = [];
    if (search)  f.push(`kërkim: "${search}"`);
    if (fWorker) f.push(`punonjësi: ${workerName(workers.find(w => String(workerId(w)) === String(fWorker)) || {}) || fWorker}`);
    if (fPeriod) f.push(`periudha: ${periods.find(p => p.key === fPeriod)?.label || fPeriod}`);
    if (fStatus) f.push(`statusi: ${statusMeta(fStatus).label}`);
    return f;
  };
  const fileStamp = () => {
    const slug = tabLabel.toLowerCase().replace(/[ëç]/g, c => (c === 'ë' ? 'e' : 'c')).replace(/[^a-z0-9]+/g, '-');
    return `rregullimet-${slug}-${new Date().toISOString().slice(0, 10)}`;
  };

  const EXPORT_HEADERS = [
    'Punonjësi', 'Kategoria', 'Lloji', 'Përshkrimi', 'Shuma (€)',
    'Periudha e pagës', 'Statusi', 'Data efektive', 'Në pagë',
  ];
  const EXPORT_HEADERS_XLS = [...EXPORT_HEADERS, 'Mbetur (€)', 'Krijoi', 'Aprovoi', 'Lista e pagës'];

  /** Shuma me shenjë: ndalesat e avancet negative, që Excel-i t'i mbledhë drejt. */
  const signedAmount = (r) => {
    const a = Number(r.amount) || 0;
    return (r.category === 'advance' || r.category === 'deduction') ? -a : a;
  };
  const baseRow = (r) => ([
    r.workerName || '—',
    CATEGORY_META[r.category]?.label || r.category,
    typeLabel(r.category, r.type),
    r.description || '',
    signedAmount(r),
    periodOf(r),
    statusMeta(r.status).label,
    r.effectiveDate ? new Date(r.effectiveDate).toLocaleDateString('sq-AL') : '',
    // Rimbursimi paguhet veçmas dhe nuk tatohet — pa këtë kolonë, totali i një
    // eksporti do të lexohej si "sa preket paga", që do të ishte gabim.
    r.category === 'reimbursement' ? 'Jo' : 'Po',
  ]);

  const exportExcel = () => {
    try { buildExcel(); }
    catch (e) { notify(`Eksporti në Excel dështoi: ${e.message}`, 'error'); }
  };

  const buildExcel = () => {
    const HEX = { ink: '111827', muted: '6B7280', rule: 'E2E8F0', brand: '4893A5', navy: '303D50', soft: 'F8FAFC', zebra: 'FCFCFD' };
    const cols = EXPORT_HEADERS_XLS;
    const N = cols.length;
    const blank = () => Array(N).fill('');
    const merges = [];
    const M = (r, c1, c2) => merges.push({ s: { r, c: c1 }, e: { r, c: c2 } });

    const aoa = [];
    const title = blank(); title[0] = `Rregullimet e pagës — ${tabLabel}`;
    aoa.push(title); M(0, 0, Math.min(N - 1, 5));

    const meta = blank();
    meta[0] = `${filtered.length} rreshta   ·   ${new Date().toLocaleString('sq-AL')}`;
    aoa.push(meta); M(1, 0, Math.min(N - 1, 5));

    const f = activeFilters();
    const filt = blank();
    filt[0] = f.length ? `Filtra: ${f.join('  ·  ')}` : 'Filtra: asnjë';
    aoa.push(filt); M(2, 0, Math.min(N - 1, 7));

    aoa.push(blank());
    aoa.push([...cols]);
    const HEAD = aoa.length - 1;

    filtered.forEach(r => aoa.push([
      ...baseRow(r),
      r.category === 'advance' && r.remainingBalance != null ? Number(r.remainingBalance) : '',
      r.createdByName || '', r.approvedByName || '', r.salaryListName || '',
    ]));

    // Totali sipas drejtimit — një shifër e vetme që përzien shtesat me ndalesat
    // nuk do të thoshte asgjë, ndaj jepen të ndara.
    aoa.push(blank());
    const sum = blank();
    sum[0] = 'TOTALI';
    sum[4] = Number((totals.additions - totals.deductions).toFixed(2));
    aoa.push(sum);
    const TOTAL = aoa.length - 1;

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [22, 14, 20, 34, 12, 16, 16, 13, 9, 12, 20, 20, 18].map(w => ({ wch: w }));
    ws['!merges'] = merges;
    ws['!rows'] = [{ hpt: 22 }];

    const at = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
    const st = (r, c, style) => { const cell = at(r, c); if (cell) cell.s = { ...(cell.s || {}), ...style }; };
    const row = (r, style) => { for (let c = 0; c < N; c++) st(r, c, style); };
    const line = (rgb, side = 'bottom', w = 'thin') => ({ [side]: { style: w, color: { rgb } } });

    row(0, { font: { bold: true, sz: 14, color: { rgb: HEX.navy } }, alignment: { vertical: 'center' } });
    row(1, { font: { sz: 9, color: { rgb: HEX.muted } } });
    row(2, { font: { sz: 9, color: { rgb: HEX.muted } }, border: line(HEX.brand, 'bottom', 'medium') });
    row(HEAD, {
      font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
      fill: { fgColor: { rgb: HEX.soft } },
      border: line(HEX.rule),
      alignment: { horizontal: 'center', wrapText: true },
    });

    // Kolona 4 është shuma; 9 është bilanci i mbetur i avancit.
    const MONEY = '#,##0.00';
    filtered.forEach((r, i) => {
      const rIdx = HEAD + 1 + i;
      const m = CATEGORY_META[r.category] || {};
      const rgb = (m.color || '#111827').slice(1).toUpperCase();
      for (let c = 0; c < N; c++) {
        st(rIdx, c, {
          font: { sz: 9, color: { rgb: HEX.ink } },
          border: line('F1F5F9'),
          ...(i % 2 ? { fill: { fgColor: { rgb: HEX.zebra } } } : {}),
        });
      }
      // Kategoria dhe shuma marrin ngjyrën e kategorisë — te letra, ngjyra e
      // sfondit të rreshtit nuk ekziston, ndaj drejtimi lexohet nga teksti.
      st(rIdx, 1, { font: { sz: 9, bold: true, color: { rgb } } });
      st(rIdx, 4, { font: { sz: 9, bold: true, color: { rgb } }, alignment: { horizontal: 'right' } });
      [4, 9].forEach((c) => { const cell = at(rIdx, c); if (cell && typeof cell.v === 'number') cell.z = MONEY; });
      st(rIdx, 8, { alignment: { horizontal: 'center' } });
    });

    row(TOTAL, { font: { bold: true, sz: 9.5, color: { rgb: HEX.navy } }, border: line(HEX.brand, 'top', 'medium') });
    st(TOTAL, 4, { alignment: { horizontal: 'right' } });
    const tc = at(TOTAL, 4); if (tc) tc.z = MONEY;

    const wb = XLSX.utils.book_new();
    // Emri i fletës: Excel-i nuk pranon më shumë se 31 shenja.
    XLSX.utils.book_append_sheet(wb, ws, tabLabel.slice(0, 31));
    XLSX.writeFile(wb, `${fileStamp()}.xlsx`);
    notify(`U eksportuan ${filtered.length} rreshta në Excel.`);
  };

  // Async tani (rasterizimi i markës). Pa këtë kapje, një dështim do të dilte
  // si "unhandled rejection" në konsolë dhe përdoruesi do të priste një skedar
  // që nuk vjen kurrë.
  const exportPDF = async () => {
    try { await buildPDF(); }
    catch (e) { notify(`Eksporti në PDF dështoi: ${e.message}`, 'error'); }
  };

  const buildPDF = async () => {
    const doc  = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const PW   = doc.internal.pageSize.getWidth();
    const PH   = doc.internal.pageSize.getHeight();
    const M    = 12;                                   // margjina
    const accent = tab === 'all' ? '#4893A5' : CATEGORY_META[tab].color;
    const rgb  = [1, 3, 5].map(i => parseInt(accent.slice(i, i + 2), 16));

    // Logoja është e KOMPANISË, jo e Nexuma-s: raporti del në emrin e saj.

    // ── Shirit koke ──────────────────────────────────────────────────────────
    doc.setFillColor(48, 61, 80);                      // #303D50, navy i markës
    doc.rect(0, 0, PW, 26, 'F');
    const logoW = drawCompanyLogo(doc, company.Logo, M, 7.5, 11, 36);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
    doc.text(`Rregullimet e pagës — ${tabLabel}`, M + (logoW ? logoW + 4 : 0), 13.5);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.setTextColor(190, 200, 210);
    const f = activeFilters();
    doc.text(
      `${filtered.length} rreshta   ·   ${new Date().toLocaleString('sq-AL')}${f.length ? `   ·   ${f.join('  ·  ')}` : ''}`,
      M + (logoW ? logoW + 4 : 0), 19.5,
      { maxWidth: PW - M * 2 - 20 },
    );
    // Vijë e hollë në ngjyrën e kategorisë — lidh kokën me tabelën poshtë.
    doc.setFillColor(...rgb); doc.rect(0, 26, PW, 1.2, 'F');
    doc.setTextColor(0);

    autoTable(doc, {
      head: [EXPORT_HEADERS],
      body: filtered.map(r => baseRow(r).map((c, i) => (i === 4 ? fmt(c) : c))),
      startY: 33,
      margin: { left: M, right: M, bottom: 16 },
      styles: { fontSize: 7, cellPadding: 1.8, lineColor: [235, 238, 242], lineWidth: 0.1, textColor: [55, 65, 81] },
      headStyles: { fillColor: [249, 250, 251], textColor: [75, 85, 99], fontSize: 6.6, fontStyle: 'bold', lineColor: [229, 231, 235], lineWidth: 0.1 },
      alternateRowStyles: { fillColor: [252, 252, 253] },
      columnStyles: {
        0: { fontStyle: 'bold' },
        3: { cellWidth: 52, textColor: [107, 114, 128] },
        4: { halign: 'right', fontStyle: 'bold' },
        8: { halign: 'center' },
      },
      // Shuma merr ngjyrën e kategorisë së VET rreshti, jo të tabit — te
      // "Të gjitha" përndryshe do të dukeshin njësoj shtesa dhe ndalesa.
      didParseCell: (d) => {
        if (d.section !== 'body') return;
        const r = filtered[d.row.index];
        const m = CATEGORY_META[r?.category];
        if (!m) return;
        if (d.column.index === 4) d.cell.styles.textColor = [1, 3, 5].map(i => parseInt(m.color.slice(i, i + 2), 16));
        if (d.column.index === 1) d.cell.styles.textColor = [1, 3, 5].map(i => parseInt(m.color.slice(i, i + 2), 16));
      },
      didDrawPage: () => {
        const p = doc.getNumberOfPages();
        doc.setFontSize(6.5); doc.setTextColor(150);
        doc.text(`${company.CompanyName || ''} · Rregullimet e pagës`.trim().replace(/^· /, ''), M, PH - 7);
        doc.text(`Faqe ${p}`, PW - M, PH - 7, { align: 'right' });
        doc.setTextColor(0);
      },
    });

    // ── Përmbledhja ──────────────────────────────────────────────────────────
    let y = (doc.lastAutoTable?.finalY || 40) + 6;
    if (y > PH - 26) { doc.addPage(); y = 20; }         // pa këtë, mbivendoset mbi footer
    const boxes = [
      { l: 'Shtesa',            v: `+${fmt(totals.additions)} €`,      c: CATEGORY_META.addition },
      { l: 'Avanc + ndalesa',   v: `-${fmt(totals.deductions)} €`,     c: CATEGORY_META.deduction },
      { l: 'Rimbursime',        v: `${fmt(totals.reimbursements)} €`,  c: CATEGORY_META.reimbursement, note: 'jashtë pagës' },
    ];
    const bw = (PW - M * 2 - 8) / 3;
    boxes.forEach((b, i) => {
      const x = M + i * (bw + 4);
      const cc = [1, 3, 5].map(k => parseInt(b.c.color.slice(k, k + 2), 16));
      doc.setFillColor(250, 251, 252); doc.setDrawColor(...b.c.border.match(/\w\w/g).map(h => parseInt(h, 16)));
      doc.roundedRect(x, y, bw, 13, 1.5, 1.5, 'FD');
      doc.setFontSize(6.2); doc.setTextColor(107, 114, 128);
      doc.text(b.l.toUpperCase() + (b.note ? ` (${b.note})` : ''), x + 3, y + 4.6);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...cc);
      doc.text(b.v, x + 3, y + 10);
      doc.setFont('helvetica', 'normal');
    });
    doc.setTextColor(0);

    doc.save(`${fileStamp()}.pdf`);
    notify(`U eksportuan ${filtered.length} rreshta në PDF.`);
  };

  // ── Dialogu ────────────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm({ ...EMPTY, category: tab === 'all' ? '' : tab, type: tab === 'all' ? '' : TYPES[tab][0].value });
    setEditing(null); setDlgOpen(true);
  };
  const openEdit = (r) => {
    setForm({
      ...r, amount: String(r.amount),
      repaymentMethod: r.repaymentMethod || 'lump_sum',
      installments: r.installments || 1,
      repaidAmount: r.repaidAmount || 0,
    });
    setEditing(r.id); setDlgOpen(true);
  };
  const pickCategory = (cat) =>
    setForm(f => ({ ...f, category: cat, type: TYPES[cat][0].value }));

  // Avancet e hapura të punonjësit — ngarkohen vetëm kur duhen, pra kur po
  // shtohet një ndalesë "Kthimi i avancit" për një punonjës të zgjedhur.
  const [openAdvances, setOpenAdvances] = useState([]);
  const needsAdvancePick = form.category === 'deduction' && form.type === 'advance_repayment';
  useEffect(() => {
    if (!needsAdvancePick || !form.workerId) { setOpenAdvances([]); return; }
    let alive = true;
    apiService().query('getOpenAdvances', `workerId=${form.workerId}`)
      .then(r => { if (alive) setOpenAdvances(r?.success ? (r.data || []) : []); })
      .catch(() => { if (alive) setOpenAdvances([]); });
    return () => { alive = false; };
  }, [needsAdvancePick, form.workerId]);

  const canSave = Boolean(form.category) && Boolean(form.workerId) && Number(form.amount) > 0
    && Boolean(form.effectiveDate) && Boolean(form.periodYear) && Boolean(form.periodMonth)
    && Boolean(String(form.description || '').trim());

  // "Dërgo për aprovim" ka kuptim vetëm kur rreshti nuk është ende në radhë. Te
  // një rresht që pret vendim, ai buton do të kthente 409 pas një ruajtjeje të
  // suksesshme — pra përdoruesi do të lexonte "Ruajtja dështoi" për një ruajtje
  // që kaloi.
  const canSend = !editing || ['draft', 'rejected'].includes(form.status);

  /**
   * Ruajtja — statusi vjen nga BUTONI, jo nga një fushë.
   *
   * `intent`: 'draft' e ruan pa e nisur procesin, 'pending' e dërgon menjëherë
   * në radhën e shqyrtimit. Më parë të dyja ishin një `<select>` brenda
   * formularit, bashkë me "aprovuar" — pra vendimi merrej me të njëjtin klikim
   * si shkrimi, dhe pa asnjë kontroll roli te serveri.
   *
   * Te një rresht që redaktohet, statusi nuk dërgohet fare: serveri e ripërsërit
   * atë që rreshti kishte, dhe dërgimi për aprovim bëhet me veprimin e vet.
   */
  const save = async (intent = 'draft') => {
    if (!canSave) return;
    setSaving(true);
    try {
      const data = { ...form, amount: parseFloat(form.amount) || 0 };
      let res;
      if (editing) {
        delete data.status;
        res = await apiService().put('payrollAdjustments', { ...data, id: editing });
      } else {
        res = await apiService().post('payrollAdjustments', { ...data, status: intent });
      }
      const id = res?.data?.id || editing;
      if (uploadFor && id) await uploadDocument(id, uploadFor);
      // Dokumenti ngarkohet PARA dërgimit për aprovim: një kërkesë që mbërrin te
      // shqyrtuesi pa faturën e vet do të kthehej vetëm për të pritur sërish.
      if (editing && intent === 'pending' && id) await runAction(id, 'submit');
      setDlgOpen(false); setUploadFor(null);
      notify(
        intent === 'pending'
          ? 'Rregullimi u dërgua për aprovim.'
          : (editing ? 'Rregullimi u përditësua.' : 'Drafti u ruajt.'),
      );
      await load();
    } catch (e) {
      notify(`Ruajtja dështoi: ${e.message}`, 'error');
    } finally { setSaving(false); }
  };

  /**
   * runAction — një hap i procesit mbi një rresht.
   *
   * Ekziston veç panelit të shqyrtimit sepse dy rrugë e kanë nevojë: "Dërgo për
   * aprovim" pas ruajtjes së një drafti, dhe butoni i shpejtë te rreshti. Vendimet
   * (aprovim/refuzim) NUK kalojnë nga këtu — atyre i duhet arsyetimi para syve,
   * ndaj shkojnë vetëm nëpër panel.
   */
  const runAction = async (id, action, reason = '') => {
    const r = await authFetch(`/api/payroll-adjustments/${id}/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || j.success === false) throw new Error(j.error || j.errorMessage || 'Veprimi dështoi.');
    return j.data;
  };

  const submitRow = async (row) => {
    try {
      await runAction(row.id, 'submit');
      notify('U dërgua për aprovim.');
      await load();
    } catch (e) { notify(e.message, 'error'); }
  };

  const uploadDocument = async (id, file) => {
    const fd = new FormData();
    fd.append('document', file);
    const r = await authFetch(`/api/payroll-adjustments/${id}/document`, { method: 'POST', body: fd });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || j.success === false) throw new Error(j.error || 'Ngarkimi i dokumentit dështoi');
  };

  const del = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await apiService().delete('payrollAdjustments', toDelete.id);
      setToDelete(null); notify('Rregullimi u fshi.'); await load();
    } catch (e) {
      notify(`Fshirja dështoi: ${e.message}`, 'error');
    } finally { setDeleting(false); }
  };

  // Pas një vendimi, lista rilexohet: statusi, aprovuesi dhe bilanci i avancit
  // ndryshojnë te serveri (trigger-i i kthimit), ndaj një përditësim vendor do
  // të tregonte shifra që nuk përputhen me pagën.
  const afterDecision = async (action, msg) => {
    setReview(null); setBulk(null); setSelected([]);
    notify(msg || DECIDED_MSG[action] || 'Vendimi u regjistrua.');
    await load();
  };

  const cat = form.category ? CATEGORY_META[form.category] : null;

  // Stili i kokës së tabelës është ai i faqeve ekzistuese (shih
  // LeaveAdjustmentsPage): i vogël, i shkruar me shkronja të mëdha, gri i qetë.
  const thSx = {
    fontWeight: 700, fontSize: '0.68rem', py: 1, px: 1.25, color: '#6B7280',
    bgcolor: '#F9FAFB', textTransform: 'uppercase', letterSpacing: '0.04em',
    borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap',
  };
  const tdSx = { py: 0.9, px: 1.25, fontSize: '0.8rem', borderBottom: '1px solid #F3F4F6' };
  const chipSx = (m) => ({
    height: 20, fontSize: '0.65rem', fontWeight: 700,
    bgcolor: m.bg, color: m.color, border: 'none',
  });

  return (
    <Box>
      <PageHeader
        title="Rregullimet e pagës"
        subtitle="Shtesat, avancet, rimbursimet dhe ndalesat — vetëm ato të aprovuara hyjnë në përllogaritjen e pagës."
        actions={
          <Button variant="contained" size="small" disableElevation startIcon={<AddIcon />}
            onClick={openAdd} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Rregullim i ri
          </Button>
        }
      />

      {/* ── Radha e shqyrtimit ────────────────────────────────────────────────
          Rri mbi tabin, jo brenda tabelës: një kërkesë që pret vendim nuk duhet
          të varet nga filtri që dikush lëshoi dje. Vetëm për këdo që vendos —
          për të tjerët do të ishte një numër pa buton. */}
      {actor.isDecider && waiting.length > 0 && !onlyPending && (
        <Alert severity="warning" variant="outlined"
          sx={{ mb: 1.5, borderRadius: 2, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
          action={
            <Button size="small" variant="contained" color="warning" disableElevation
              onClick={showQueue} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Shqyrto
            </Button>
          }>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
            {waiting.length} {waiting.length === 1 ? 'rregullim pret' : 'rregullime presin'} vendim
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Derisa të aprovohen, nuk hyjnë në përllogaritjen e pagës.
          </Typography>
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
        sx={{
          mb: 1.5, minHeight: 38,
          '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', px: 1.75 },
          '& .MuiTabs-indicator': { height: 2, bgcolor: tab === 'all' ? 'primary.main' : CATEGORY_META[tab].color },
        }}>
        <Tab value="all" label={`Të gjitha · ${countBy('all')}`} />
        {CATEGORIES.map(c => (
          <Tab key={c} value={c}
            label={`${CATEGORY_META[c].label} · ${countBy(c)}`}
            sx={{ '&.Mui-selected': { color: CATEGORY_META[c].color } }} />
        ))}
      </Tabs>

      {/* Një Paper i vetëm mban filtrat, totalet dhe tabelën — jo tri kuti të
          ndara njëra mbi tjetrën, si te faqet e tjera të Nexuma-s. */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
        {/* ── Filtra ── */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
          <TextField size="small" placeholder="Kërko punonjës, lloj, përshkrim…" value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', sm: 240 }, '& .MuiOutlinedInput-root': { fontSize: '0.82rem' } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#9CA3AF' }} /></InputAdornment> }} />
          <TextField select size="small" label="Punonjësi" value={fWorker} onChange={e => setFWorker(e.target.value)} sx={{ width: 170 }}>
            <MenuItem value="">Të gjithë</MenuItem>
            {activeWorkers.map(w => <MenuItem key={workerId(w)} value={workerId(w)}>{workerName(w)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Periudha" value={fPeriod} onChange={e => setFPeriod(e.target.value)} sx={{ width: 150 }}>
            <MenuItem value="">Të gjitha</MenuItem>
            {periods.map(p => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Statusi" value={fStatus} onChange={e => setFStatus(e.target.value)} sx={{ width: 160 }}>
            <MenuItem value="">Të gjitha</MenuItem>
            {STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          {(search || fWorker || fStatus || fPeriod) && (
            <Chip size="small" label="Pastro" variant="outlined" onClick={clearFilters} onDelete={clearFilters}
              sx={{ height: 24, fontSize: '0.7rem' }} />
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
            {filtered.length} nga {rows.length}
          </Typography>
          {/* Butonat rrinë te filtrat e jo te koka e faqes, sepse veprojnë mbi
              atë që është filtruar — jo mbi gjithë bazën. */}
          {/* Grup i lidhur: një veprim në dy formate, jo dy butona të palidhur.
              `outlineOffset: -2px` e mban unazën e fokusit brenda butonit — pa të
              ajo rrinte 2px jashtë dhe dukej si kornizë e ngecur. */}
          <Box sx={{
            display: 'inline-flex', border: '1px solid', borderColor: 'divider',
            borderRadius: 1.5, overflow: 'hidden',
            '& .MuiButton-root': {
              textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
              py: 0.4, px: 1.25, borderRadius: 0, border: 0, color: '#475569',
              '&:hover': { bgcolor: '#F1F5F9' },
              '&:focus-visible': { outlineOffset: '-2px' },
            },
            '& .MuiButton-root:first-of-type': { borderRight: '1px solid', borderColor: 'divider' },
          }}>
            <Tooltip title={`Shkarko ${filtered.length} rreshtat e shfaqur si Excel`}>
              <span>
                <Button size="small" startIcon={<GridOnIcon sx={{ fontSize: 15 }} />}
                  onClick={exportExcel} disabled={!filtered.length}>Excel</Button>
              </span>
            </Tooltip>
            <Tooltip title={`Shkarko ${filtered.length} rreshtat e shfaqur si PDF`}>
              <span>
                <Button size="small" startIcon={<PictureAsPdfIcon sx={{ fontSize: 15 }} />}
                  onClick={exportPDF} disabled={!filtered.length}>PDF</Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Totalet ──────────────────────────────────────────────────────
            Shirit i qetë, jo katër karta me korniza të trasha. Ato konkurronin
            me tabelën, që është përmbajtja e vërtetë e faqes; ngjyra tani rri
            vetëm te shifra dhe te një vijë e hollë majtas. */}
        <Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 2.5, flexWrap: 'wrap', borderBottom: '1px solid #F3F4F6' }}>
          {[
            { label: 'Shtesa',          val: totals.additions,      meta: CATEGORY_META.addition,      sign: '+' },
            { label: 'Avanc + ndalesa', val: totals.deductions,     meta: CATEGORY_META.deduction,     sign: '−' },
            { label: 'Rimbursime',      val: totals.reimbursements, meta: CATEGORY_META.reimbursement, sign: ''  },
          ].map(c => (
            <Box key={c.label} sx={{ pl: 1.25, borderLeft: '3px solid', borderColor: c.meta.color, minWidth: 118 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {c.label}
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: c.meta.color, lineHeight: 1.35 }}>
                {c.sign}€{fmt(c.val)}
              </Typography>
            </Box>
          ))}
          {totals.pending > 0 && (
            // Numri është filtër, jo dekor: shtypja e tij e hap radhën. Element
            // `button` me qëllim — një `<Box onClick>` nuk arrihet me tastierë
            // (shih docs/notes/ui_conventions.md).
            <Box component="button" type="button" onClick={showQueue}
              aria-label={`Shfaq ${totals.pending} rregullime që presin aprovim`}
              sx={{
                pl: 1.25, py: 0, minWidth: 118, textAlign: 'left', cursor: 'pointer',
                border: 0, borderLeft: '3px solid #F59E0B', bgcolor: 'transparent',
                font: 'inherit', '&:hover': { bgcolor: '#FFFBEB' },
              }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Presin aprovim
              </Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#B45309', lineHeight: 1.35 }}>
                {totals.pending}
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Shiriti i zgjedhjes ───────────────────────────────────────────────
            Del vetëm kur ka zgjedhje. Vendimi për shumë rreshta hapet në dialog
            që i LISTON ato me arsyetimin e secilit — numri nuk aprovohet vetë. */}
        {actor.isDecider && selected.length > 0 && (
          <Box sx={{
            px: 2, py: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
            bgcolor: '#EFF6FF', borderBottom: '1px solid #DBEAFE',
          }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1E40AF' }}>
              {selected.length} {selected.length === 1 ? 'i zgjedhur' : 'të zgjedhur'}
            </Typography>
            <Chip size="small" label="Pastro" variant="outlined" onClick={() => setSelected([])}
              sx={{ height: 24, fontSize: '0.7rem' }} />
            <Box sx={{ flex: 1 }} />
            <Button size="small" variant="contained" color="success" disableElevation
              startIcon={<CheckCircleRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setBulk('approve')}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Aprovo
            </Button>
            <Button size="small" variant="outlined" color="error"
              startIcon={<CancelRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setBulk('reject')}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Refuzo
            </Button>
          </Box>
        )}

        {tab !== 'all' && (
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #F3F4F6', bgcolor: CATEGORY_META[tab].soft }}>
            <Typography variant="caption" sx={{ color: '#4B5563' }}>
              <strong style={{ color: CATEGORY_META[tab].color }}>{CATEGORY_META[tab].label}</strong> — {CATEGORY_META[tab].hint}
            </Typography>
          </Box>
        )}

        {/* ── Tabela e përbashkët ── */}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 940 }}>
            <TableHead>
              <TableRow>
                {actor.isDecider && (
                  <TableCell padding="checkbox" sx={{ ...thSx, width: 40 }}>
                    <Tooltip title={decidable.length ? 'Zgjedh të gjitha që presin vendim' : 'Asnjë rresht që pret vendim'}>
                      <span>
                        <Checkbox size="small" disabled={!decidable.length}
                          checked={allPicked}
                          indeterminate={selected.length > 0 && !allPicked}
                          onChange={toggleAll} />
                      </span>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell sx={thSx}>Punonjësi</TableCell>
                <TableCell sx={thSx}>Kategoria</TableCell>
                <TableCell sx={thSx}>Lloji</TableCell>
                <TableCell sx={thSx}>Përshkrimi</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right' }}>Shuma</TableCell>
                <TableCell sx={thSx}>Periudha e pagës</TableCell>
                <TableCell sx={thSx}>Statusi</TableCell>
                <TableCell sx={thSx}>Data</TableCell>
                <TableCell sx={{ ...thSx, textAlign: 'right', width: 150 }}>Veprimet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={actor.isDecider ? 10 : 9} align="center" sx={{ py: 6, border: 0 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={actor.isDecider ? 10 : 9} align="center" sx={{ py: 6, border: 0 }}>
                  <TuneIcon sx={{ fontSize: 40, color: '#D1D5DB', display: 'block', mx: 'auto', mb: 1 }} />
                  <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', fontWeight: 600 }}>
                    {rows.length === 0 ? 'Ende asnjë rregullim' : 'Asnjë rregullim për filtrat e zgjedhur'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#9CA3AF', mt: 0.5 }}>
                    {rows.length === 0
                      ? 'Shto shtesa, avance, rimbursime ose ndalesa me “Rregullim i ri”.'
                      : 'Provo t’i heqësh disa filtra.'}
                  </Typography>
                </TableCell></TableRow>
              )}
              {!loading && filtered.map(r => {
                const m = CATEGORY_META[r.category] || {};
                const locked = r.status === 'processed';
                const pickable = canRun('approve', r, actor) || canRun('reject', r, actor);
                const sendable = canRun('submit', r, actor);
                return (
                  <TableRow key={r.id} hover
                    selected={selected.includes(r.id)}
                    sx={{ '&:hover': { bgcolor: '#FAFBFC' },
                      // Shirit i hollë majtas në ngjyrën e kategorisë — drejtimi
                      // i rreshtit lexohet edhe kur syri rrëshqet poshtë kolonës.
                      borderLeft: `3px solid ${m.bg}` }}>
                    {actor.isDecider && (
                      <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #F3F4F6' }}>
                        <Tooltip title={pickable ? '' : 'Nuk pret vendim nga ti'}>
                          <span>
                            <Checkbox size="small" disabled={!pickable}
                              checked={selected.includes(r.id)}
                              onChange={() => toggleRow(r.id)} />
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell sx={{ ...tdSx, fontWeight: 600 }}>
                      {r.workerName || <Chip size="small" label="Punëtor i fshirë" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#FEF3C7', color: '#B45309' }} />}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Chip size="small" label={m.label} sx={chipSx(m)} />
                    </TableCell>
                    <TableCell sx={tdSx}>{typeLabel(r.category, r.type)}</TableCell>
                    <TableCell sx={{ ...tdSx, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6B7280' }}>
                      <Tooltip title={r.description || ''}><span>{r.description || '—'}</span></Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: m.color, whiteSpace: 'nowrap' }}>
                        {m.sign}€{fmt(r.amount)}
                      </Typography>
                      {r.category === 'advance' && r.remainingBalance != null && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#9CA3AF' }}>
                          mbetur €{fmt(r.remainingBalance)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap', color: '#4B5563' }}>{periodOf(r)}</TableCell>
                    <TableCell sx={tdSx}>
                      {/* Statusi vjen nga StatusChip — ikonë + fjalë + ton, jo
                          vetëm ngjyrë. Arsyeja e refuzimit rri te tooltip-i, që
                          "Refuzuar" të mos jetë fjalë pa shpjegim. */}
                      <Tooltip title={
                        locked && r.salaryListName ? `Procesuar te "${r.salaryListName}"`
                          : r.status === 'rejected' ? (r.rejectionReason || 'Pa arsye të regjistruar')
                          : r.approvedByName ? `Nga ${r.approvedByName}` : ''
                      }>
                        <span><StatusChip status={r.status} size="sm" /></span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: 'nowrap', color: '#6B7280' }}>{fmtDate(r.effectiveDate)}</TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {r.documentPath && (
                        <Tooltip title={r.documentName || 'Shkarko dokumentin'}>
                          <IconButton size="small" onClick={() => window.open(`${process.env.REACT_APP_API_URL || ''}/api/payroll-adjustments/${r.id}/document`, '_blank')}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Drafti dërgohet drejt e nga rreshti: dërgimi nuk është
                          vendim, ndaj nuk ka pse t'i kalojë panelit. */}
                      {sendable && (
                        <Tooltip title="Dërgo për aprovim">
                          <IconButton size="small" color="primary" onClick={() => submitRow(r)}>
                            <SendRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {/* Aprovimi me një klikim nga rreshti nuk ekziston me qëllim:
                          "Shqyrto" çon te paneli, ku arsyetimi rri para vendimit. */}
                      <Tooltip title="Shqyrto — detajet, kronologjia, vendimi">
                        <IconButton size="small"
                          color={r.status === 'pending' && actor.isDecider ? 'warning' : 'default'}
                          onClick={() => setReview(r)}>
                          <FactCheckOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={locked ? 'Procesuar në pagë — nuk ndryshohet' : 'Ndrysho'}>
                        <span><IconButton size="small" disabled={locked} onClick={() => openEdit(r)}><EditIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                      <Tooltip title={locked ? 'Procesuar në pagë — nuk fshihet' : 'Fshij'}>
                        <span><IconButton size="small" color="error" disabled={locked} onClick={() => setToDelete(r)}><DeleteIcon fontSize="small" /></IconButton></span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── Dialogu: kategoria e para, pastaj fushat ── */}
      <Dialog open={dlgOpen} onClose={() => !saving && setDlgOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>
          {editing ? 'Ndrysho rregullimin' : 'Rregullim i ri'}
        </DialogTitle>
        <DialogContent dividers>
          {!form.category ? (
            // Hapi 1 — pa kategori, fushat s'kanë kuptim: një avanc dhe një
            // rimbursim nuk kërkojnë të njëjtat gjëra.
            <>
              <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
                Zgjedh llojin e rregullimit:
              </Typography>
              <Grid container spacing={1.5}>
                {CATEGORIES.map(c => {
                  const m = CATEGORY_META[c];
                  return (
                    <Grid item xs={12} sm={6} key={c}>
                      <Card variant="outlined" sx={{
                        borderRadius: 2, borderColor: m.border, bgcolor: m.soft, height: '100%',
                        transition: 'border-color .15s, box-shadow .15s',
                        '&:hover': { borderColor: m.color, boxShadow: `0 0 0 1px ${m.border}` },
                      }}>
                        <CardActionArea sx={{ p: 1.5, height: '100%', alignItems: 'flex-start' }} onClick={() => pickCategory(c)}>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: m.color }}>
                            {m.sign} {m.singular}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', mt: 0.25, display: 'block' }}>
                            {m.hint}
                          </Typography>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Stack spacing={2} mt={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip label={cat.label} sx={{ bgcolor: cat.bg, color: cat.color, fontWeight: 700, border: '1px solid', borderColor: cat.border }} />
                {!editing && <Button size="small" onClick={() => setForm(f => ({ ...f, category: '', type: '' }))} sx={{ textTransform: 'none' }}>Ndrysho kategorinë</Button>}
              </Stack>

              <WorkerPicker workers={activeWorkers} value={form.workerId}
                onChange={v => setForm(f => ({ ...f, workerId: v }))} label="Punonjësi" required />

              <TextField select size="small" label="Lloji i rregullimit" required value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                {TYPES[form.category].map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField size="small" label="Shuma (€)" required type="number" sx={{ flex: 1 }}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                <TextField size="small" label="Data efektive" required type="date" sx={{ flex: 1 }}
                  InputLabelProps={{ shrink: true }}
                  value={form.effectiveDate} onChange={e => setForm(f => ({ ...f, effectiveDate: e.target.value }))} />
              </Stack>

              <Stack direction="row" spacing={2}>
                <TextField select size="small" label="Muaji i pagës" required sx={{ flex: 1 }}
                  value={form.periodMonth} onChange={e => setForm(f => ({ ...f, periodMonth: Number(e.target.value) }))}>
                  {MONTHS_SQ.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
                </TextField>
                <TextField size="small" label="Viti i pagës" required type="number" sx={{ width: 120 }}
                  inputProps={{ min: 2000, max: 2100 }}
                  value={form.periodYear} onChange={e => setForm(f => ({ ...f, periodYear: Number(e.target.value) }))} />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
                Periudha e pagës ndahet nga data efektive me qëllim — një shpenzim i fundmuajit mund të paguhet me pagën e muajit tjetër.
              </Typography>

              {form.category === 'advance' && (
                <>
                  <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Kthimi</Typography></Divider>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField select size="small" label="Mënyra e kthimit" sx={{ flex: 1 }}
                      value={form.repaymentMethod} onChange={e => setForm(f => ({ ...f, repaymentMethod: e.target.value }))}>
                      {REPAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Numri i kësteve" type="number" sx={{ width: 140 }}
                      inputProps={{ min: 1, max: 60 }}
                      disabled={form.repaymentMethod !== 'installments'}
                      value={form.installments} onChange={e => setForm(f => ({ ...f, installments: Number(e.target.value) }))} />
                  </Stack>
                  {/* Të dyja janë të nxjerra, jo të shkruara: shuma e kthyer
                      mblidhet nga ndalesat "Kthimi i avancit" që e lidhin këtë
                      avanc, dhe bilanci del prej saj. Një fushë e redaktueshme
                      këtu do të thoshte dy bilance dhe asnjë mënyrë për të ditur
                      cili është i vërteti. */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField size="small" label="Shuma e kthyer (€)" sx={{ flex: 1 }} disabled
                      value={fmt(form.repaidAmount || 0)}
                      helperText="Mblidhet nga ndalesat e kthimit" />
                    <TextField size="small" label="Bilanci i mbetur (€)" sx={{ flex: 1 }} disabled
                      value={fmt(Math.max(0, (parseFloat(form.amount) || 0) - (parseFloat(form.repaidAmount) || 0)))}
                      helperText="Llogaritet — nuk shkruhet me dorë" />
                  </Stack>
                </>
              )}

              {needsAdvancePick && (
                <TextField select size="small" label="Cilin avanc e kthen"
                  value={form.repaysAdjustmentId || ''}
                  onChange={e => setForm(f => ({ ...f, repaysAdjustmentId: e.target.value || null }))}
                  helperText={!form.workerId
                    ? 'Zgjedh punonjësin së pari'
                    : openAdvances.length
                      ? 'Bilanci i avancit ulet vetë kur kjo ndalesë aprovohet'
                      : 'Ky punonjës nuk ka avanc të hapur'}>
                  <MenuItem value=""><em>Pa lidhje me një avanc</em></MenuItem>
                  {openAdvances.map(a => (
                    <MenuItem key={a.id} value={a.id}>
                      {MONTHS_SQ[(a.periodMonth || 1) - 1]} {a.periodYear} · €{fmt(a.amount)} · mbetur €{fmt(a.remaining)}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField size="small" label="Arsyeja ose përshkrimi" required multiline rows={2}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                helperText="Kjo arsye lexohet nga ai që vendos — dhe nga punonjësi." />

              {/* Fusha "Statusi" u hoq nga këtu me qëllim. Aprovimi nuk është
                  vlerë që zgjidhet në të njëjtin formular ku shkruhet shuma:
                  statusin e vendos veprimi ("Ruaj si draft" / "Dërgo për
                  aprovim") dhe pastaj procesi, ku rri gardhi i rolit dhe
                  rregulli që askush nuk vendos për rreshtin e vet. */}
              {editing && (
                <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, py: 0.25 }}>
                  <Typography sx={{ fontSize: '0.78rem' }}>
                    Statusi tani është <strong>{statusMeta(form.status).label}</strong> — ndryshimet e tij
                    bëhen te “Shqyrto”, jo këtu.
                  </Typography>
                </Alert>
              )}

              {DOCUMENT_CATEGORIES.has(form.category) && (
                <Box>
                  <input ref={fileRef} type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setUploadFor(e.target.files?.[0] || null)} />
                  <Button size="small" variant="outlined" startIcon={<AttachFileIcon />}
                    onClick={() => fileRef.current?.click()} sx={{ textTransform: 'none' }}>
                    {uploadFor ? uploadFor.name : (form.documentName || 'Bashkëngjit faturë ose dëshmi')}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    PDF, JPG ose PNG · deri në 5 MB
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        {/* Dy veprime ruajtjeje në vend të një "Ruaj" me status të zgjedhur:
            hapi tjetër i procesit lexohet nga butoni. Drafti mbetet i pashikuar
            nga kushdo tjetër; dërgimi e vë në radhën e shqyrtimit. */}
        <DialogActions sx={{ px: 2, py: 1.5, gap: 0.5 }}>
          <Button onClick={() => { setDlgOpen(false); setUploadFor(null); }} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>Anulo</Button>
          <Box sx={{ flex: 1 }} />
          {form.category && (
            <>
              <Button variant={canSend ? 'outlined' : 'contained'} disableElevation
                onClick={() => save('draft')} disabled={saving || !canSave}
                startIcon={saving && !canSend ? <CircularProgress size={15} color="inherit" /> : null}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                {editing ? 'Ruaj ndryshimet' : 'Ruaj si draft'}
              </Button>
              {canSend && (
                <Button variant="contained" disableElevation onClick={() => save('pending')} disabled={saving || !canSave}
                  startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SendRoundedIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  {saving ? 'Duke ruajtur…' : 'Dërgo për aprovim'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Shqyrtimi dhe vendimi ── */}
      <AdjustmentReviewDialog
        open={Boolean(review)}
        // Rreshti merret gjithnjë nga `rows`: pas një rifreskimi, ai i mbajtur
        // në state do të tregonte statusin e para vendimit.
        row={review ? (rows.find(r => r.id === review.id) || review) : null}
        actor={actor}
        // Vendimi del në emër të kompanisë, ndaj koka e PDF-it merr logon,
        // adresën dhe NUI-n e saj — jo markën e Nexuma-s.
        company={company}
        onClose={() => setReview(null)}
        onEdit={(r) => { setReview(null); openEdit(r); }}
        onDone={(action) => afterDecision(action)}
        onNotify={(msg, sev) => notify(msg, sev || 'success')}
      />

      <AdjustmentBulkDecideDialog
        open={Boolean(bulk)}
        action={bulk}
        rows={selectedRows}
        actor={actor}
        onClose={() => setBulk(null)}
        onDone={(action, data) => afterDecision(
          action,
          `${DECIDED_MSG[action]} (${data?.done || 0} ${data?.done === 1 ? 'rregullim' : 'rregullime'})`,
        )}
      />

      {/* Dialogu i vjetër "Historiku i ndryshimeve" u hoq: lexonte `audit_log`
          dhe tregonte vetëm "payrollAdjustments.update" pa gjendjen nga-ku/ku.
          Kronologjia e vërtetë — kërkesa, dërgimi, vendimi dhe arsyeja — rri te
          paneli i shqyrtimit, mbi `payroll_adjustment_events`. Dy historikë që
          nuk pajtohen janë më keq se një. */}

      <ConfirmDialog
        open={Boolean(toDelete)} busy={deleting}
        title="Fshij rregullimin" message="Ky veprim nuk kthehet."
        confirmLabel="Fshij" busyLabel="Duke fshirë…"
        onConfirm={del} onClose={() => setToDelete(null)}
        details={toDelete && (
          <Stack spacing={0.75}>
            {[['Punonjësi', toDelete.workerName || '—'],
              ['Kategoria', CATEGORY_META[toDelete.category]?.label || '—'],
              ['Shuma', `€${fmt(toDelete.amount)}`],
              ['Periudha', periodOf(toDelete)]].map(([k, v]) => (
              <Stack key={k} direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">{k}</Typography>
                <Typography variant="body2" fontWeight={700}>{v}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      />

      <Snackbar open={snack.open} autoHideDuration={5000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {/* `variant="filled"` e nxirrte një kuti jeshile me kornizë të fortë —
            një njoftim kalimtar nuk ka pse të bërtasë. */}
        <Alert severity={snack.sev} variant="standard" onClose={() => setSnack(s => ({ ...s, open: false }))}
          sx={{ borderRadius: 2, boxShadow: '0 6px 20px rgba(15,23,42,.12)', border: '1px solid', borderColor: 'divider' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
